import { AxiosError } from 'axios';
import { ClipboardEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { registerUser, resendVerificationOtp, updateConsentStatus, verifyEmailOtp } from '@/lib/apiService';
import { AuthResponse, RegisterRequest } from '@/types/auth';
import { getIanaTimeZone } from '@/utils/appointments/appointment-timezone';
import { getDefaultAuthenticatedRoute, hasFullAppAccess } from '@/lib/accessControl';
import {
  REGISTRATION_BLOCKED_MESSAGE,
  clearRegistrationBlockedFromUrl,
  isRegistrationNotAllowedError,
  readRegistrationBlockedFromUrl,
} from '@/lib/auth/registrationGate';
import {
  CONSENT_REQUIRED_ROUTE,
  clearConsentGateState,
  persistConsentGateState,
} from '@/lib/consentGate';
import {
  clearSubscriptionGateState,
} from '@/lib/subscriptionGate';
import {
  clearStoredSignupConsent,
  buildSignupConsentSnapshot,
  getSignupConsentUpdatePayload,
  persistSignupConsent,
  readStoredSignupConsent,
} from '@/lib/signupConsent';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { applyOtpInputValue, applyOtpPasteValue, isOtpSubmitKey } from '@/utils/otp-input';

const OTP_LENGTH = 6;
const VERIFICATION_EMAIL_STORAGE_KEY = 'caloriescience_email_verification_email';
export const SIGNUP_COMPLETION_PATH = '/signup/step-1';

type SignupRouteState = {
  autoResendVerification?: boolean;
  verificationEmail?: string;
  signupCompleted?: boolean;
} | null;

const normalizeVerificationEmail = (value: string | null | undefined): string => {
  return value?.trim().toLowerCase() ?? '';
};

const readStoredVerificationEmail = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  return normalizeVerificationEmail(window.localStorage.getItem(VERIFICATION_EMAIL_STORAGE_KEY));
};

const persistVerificationEmail = (email: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedEmail = normalizeVerificationEmail(email);
  if (normalizedEmail) {
    window.localStorage.setItem(VERIFICATION_EMAIL_STORAGE_KEY, normalizedEmail);
    return;
  }

  window.localStorage.removeItem(VERIFICATION_EMAIL_STORAGE_KEY);
};

const readRetryAfterSeconds = (error: unknown): number => {
  if (!(error instanceof AxiosError)) {
    return 0;
  }
  const retryAfterSeconds = Number(error.response?.data?.retryAfterSeconds);
  if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
    return 0;
  }
  return Math.ceil(retryAfterSeconds);
};

export const registrationSchema = z.object({
  fullName: z.string().trim().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(12, "Password must be at least 12 characters"),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "Please confirm that you have read and agree to the applicable agreements and policies",
  }),
  acceptPrivacy: z.boolean().refine((val) => val === true, {
    message: "Please confirm that the declarations are True and Accurate",
  }),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

export type RegistrationFormValues = z.infer<typeof registrationSchema>;

type SignUpStep = 'register' | 'verify' | 'success';

export const useSignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeState = location.state as SignupRouteState;
  const queryVerificationEmail = normalizeVerificationEmail(searchParams.get('verifyEmail'));
  const stateVerificationEmail = normalizeVerificationEmail(routeState?.verificationEmail);
  const isSignupCompletionRoute = location.pathname === SIGNUP_COMPLETION_PATH;
  const routeSignupCompleted = isSignupCompletionRoute && routeState?.signupCompleted === true;
  const initialVerificationEmail = queryVerificationEmail || stateVerificationEmail || readStoredVerificationEmail();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [step, setStep] = useState<SignUpStep>(() => (
    routeSignupCompleted ? 'success' : initialVerificationEmail ? 'verify' : 'register'
  ));
  const [verificationEmail, setVerificationEmail] = useState(initialVerificationEmail);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const autoResendEmailRef = useRef<string | null>(null);

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    mode: 'onChange',
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
      acceptPrivacy: false,
    }
  });

  const handleConsentBlocked = useCallback((response: AuthResponse): boolean => {
    const consentStatus = response.consent;
    if (!consentStatus?.accessBlocked) {
      clearConsentGateState();
      return false;
    }

    const returnTo = hasFullAppAccess(response.user)
      ? '/onboarding/practice-type/institution'
      : getDefaultAuthenticatedRoute(response.user);
    persistConsentGateState({
      accessBlocked: true,
      reason: consentStatus.reason ?? null,
      returnTo,
    });

    showWarningToast(
      'Consent required',
      'Please review and accept terms and privacy consent to continue.'
    );
    navigate(`${CONSENT_REQUIRED_ROUTE}?from=${encodeURIComponent(returnTo)}`);
    return true;
  }, [navigate]);

  const handleSubscriptionBlocked = useCallback((response: AuthResponse): boolean => {
    const subscriptionStatus = response.subscription;
    if (!subscriptionStatus?.accessBlocked) {
      clearSubscriptionGateState();
      return false;
    }

    showWarningToast(
      'Subscription required',
      subscriptionStatus.message || 'Please choose a subscription plan to continue.'
    );
    return true;
  }, []);

  useEffect(() => {
    const blockedRouteState = location.state as { registrationBlocked?: boolean } | null;
    if (blockedRouteState?.registrationBlocked) {
      form.setError('email', { message: REGISTRATION_BLOCKED_MESSAGE });
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
        },
        { replace: true, state: null }
      );
      return;
    }

    if (step !== 'register' || !readRegistrationBlockedFromUrl()) {
      return;
    }

    clearRegistrationBlockedFromUrl();
    form.setError('email', { message: REGISTRATION_BLOCKED_MESSAGE });
    showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
  }, [form, location.pathname, location.search, location.state, navigate, step]);

  useEffect(() => {
    if (!isSignupCompletionRoute || step === 'success') {
      return;
    }

    navigate('/signup', { replace: true, state: null });
  }, [isSignupCompletionRoute, navigate, step]);

  const onSubmit = useCallback(async (data: RegistrationFormValues) => {
    setIsSubmitting(true);
    try {
      const normalizedName = data.fullName.trim().replace(/\s+/g, ' ');
      const registerData: RegisterRequest = {
        fullName: normalizedName,
        email: normalizeVerificationEmail(data.email),
        password: data.password,
        timezone: getIanaTimeZone(),
        consent: getSignupConsentUpdatePayload(
          buildSignupConsentSnapshot(normalizeVerificationEmail(data.email))
        ),
      };

      await registerUser(registerData);
      setVerificationEmail(registerData.email);
      persistVerificationEmail(registerData.email);
      persistSignupConsent(registerData.email);
      setOtp(Array(OTP_LENGTH).fill(''));
      setStep('verify');
      showSuccessToast('Verification code sent', 'Enter the OTP sent to your email.');
    } catch (error) {
      if (isRegistrationNotAllowedError(error)) {
        form.setError('email', { message: REGISTRATION_BLOCKED_MESSAGE });
        showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
        return;
      }

      showErrorToast(error, 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [form]);

  const handleVerifyOtp = useCallback(async () => {
    const enteredOtp = otp.join('');
    if (!verificationEmail) {
      showWarningToast('Missing email', 'Please register again.');
      return;
    }

    if (enteredOtp.length < OTP_LENGTH) {
      showWarningToast('Incomplete OTP', 'Please enter the 6-digit code.');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      let response = await verifyEmailOtp({
        email: verificationEmail,
        otp: enteredOtp,
      });
      persistVerificationEmail('');

      const signupConsent = readStoredSignupConsent(verificationEmail);
      if (response.consent?.accessBlocked && signupConsent) {
        try {
          const consentResponse = await updateConsentStatus(
            getSignupConsentUpdatePayload(signupConsent)
          );
          response = {
            ...response,
            consent: consentResponse.consent,
          };
          clearStoredSignupConsent(verificationEmail);
        } catch (consentError) {
          showErrorToast(consentError, 'Could not save consent');
        }
      } else {
        clearStoredSignupConsent(verificationEmail);
      }

      if (handleConsentBlocked(response)) {
        return;
      }
      if (handleSubscriptionBlocked(response)) {
        return;
      }
      if (!hasFullAppAccess(response.user)) {
        showSuccessToast('Email verified', 'Your account has been verified.');
        navigate(getDefaultAuthenticatedRoute(response.user), { replace: true });
        return;
      }
      setStep('success');
      navigate(SIGNUP_COMPLETION_PATH, {
        replace: true,
        state: {
          signupCompleted: true,
        },
      });
      showSuccessToast('Email verified', 'Your account has been verified.');
    } catch (error) {
      showErrorToast(error, 'OTP verification failed');
    } finally {
      setIsVerifyingOtp(false);
    }
  }, [handleConsentBlocked, handleSubscriptionBlocked, navigate, otp, verificationEmail]);

  const requestVerificationOtp = useCallback(async (email: string, successTitle = 'Verification code resent') => {
    const normalizedEmail = normalizeVerificationEmail(email);
    if (!normalizedEmail) {
      return;
    }

    setIsResendingOtp(true);
    try {
      await resendVerificationOtp({ email: normalizedEmail });
      setOtp(Array(OTP_LENGTH).fill(''));
      persistVerificationEmail(normalizedEmail);
      showSuccessToast(successTitle, 'Please check your email.');
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 429) {
        const retryAfterSeconds = readRetryAfterSeconds(error);
        showWarningToast(
          'Please wait',
          retryAfterSeconds > 0
            ? `Try again in ${retryAfterSeconds} seconds.`
            : 'Please wait before requesting another OTP.'
        );
        return;
      }

      showErrorToast(error, 'Could not resend OTP');
    } finally {
      setIsResendingOtp(false);
    }
  }, []);

  useEffect(() => {
    const emailFromRoute = queryVerificationEmail || stateVerificationEmail;
    if (!emailFromRoute) {
      return;
    }

    setVerificationEmail(emailFromRoute);
    persistVerificationEmail(emailFromRoute);
    setStep('verify');

    const shouldAutoResend =
      searchParams.get('resend') === '1' || routeState?.autoResendVerification === true;
    if (!shouldAutoResend || autoResendEmailRef.current === emailFromRoute) {
      return;
    }

    autoResendEmailRef.current = emailFromRoute;
    void requestVerificationOtp(emailFromRoute, 'Verification code sent');

    if (searchParams.get('resend') === '1') {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('resend');
      const nextSearch = nextSearchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        {
          replace: true,
          state: { verificationEmail: emailFromRoute },
        }
      );
    }
  }, [
    location.pathname,
    navigate,
    queryVerificationEmail,
    requestVerificationOtp,
    routeState?.autoResendVerification,
    searchParams,
    stateVerificationEmail,
  ]);

  const handleResendOtp = useCallback(async () => {
    await requestVerificationOtp(verificationEmail);
  }, [requestVerificationOtp, verificationEmail]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleOtpChange = useCallback((index: number, value: string) => {
    setOtp((previous) => {
      const { next, focusIndex } = applyOtpInputValue(previous, index, value, OTP_LENGTH);
      window.setTimeout(() => {
        otpRefs.current[focusIndex]?.focus();
        otpRefs.current[focusIndex]?.select?.();
      }, 0);
      return next;
    });
  }, []);

  const handleOtpKeyDown = useCallback((index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (isOtpSubmitKey(event.key)) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
      return;
    }
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }, [otp]);

  const handleOtpPaste = useCallback((event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const nextValues = applyOtpPasteValue(event.clipboardData.getData('text'), OTP_LENGTH);
    if (!nextValues) return;

    setOtp(nextValues);
    const filledCount = nextValues.filter(Boolean).length;
    const nextFocusIndex = Math.min(Math.max(filledCount, 1), OTP_LENGTH) - 1;
    otpRefs.current[nextFocusIndex]?.focus();
  }, []);

  const goBackToRegistration = useCallback(() => {
    setStep('register');
    setVerificationEmail('');
    persistVerificationEmail('');
    setOtp(Array(OTP_LENGTH).fill(''));
    navigate('/signup', { replace: true, state: null });
  }, [navigate]);

  const passwordsMatch = form.watch('password') === form.watch('confirmPassword') && 
    form.watch('password') !== '' && 
    form.watch('confirmPassword') !== '';

  return {
    form,
    step,
    verificationEmail,
    otp,
    otpRefs,
    showPassword,
    showConfirmPassword,
    isSubmitting,
    isVerifyingOtp,
    isResendingOtp,
    passwordsMatch,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerifyOtp,
    handleResendOtp,
    goBackToRegistration,
    onSubmit,
  };
};
